/**
 * Payment Callback Page
 * Handles redirect from Paystack after payment
 * Verifies contribution status and redirects to success or error
 */

import { Spinner, useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { pollContributionStatus } from '../services/api';
import Header from './header';

function PaymentCallback() {
    const navigate = useNavigate();
    const toast = useToast();
    const [status, setStatus] = useState<'verifying' | 'success' | 'failed'>('verifying');
    const [message, setMessage] = useState('Verifying your payment...');

    const verifyPayment = useCallback(async () => {
        // Get contribution ID from localStorage (saved before Paystack redirect)
        const contributionId = localStorage.getItem('MOTITO_CONTRIBUTION_ID');

        if (!contributionId) {
            console.error('No contribution ID found');
            setStatus('failed');
            setMessage('Payment verification failed. Please try again.');
            toast({
                title: 'Verification failed',
                description: 'Could not find payment information',
                status: 'error',
                duration: 5000,
                position: 'top',
            });
            // Redirect back to campaign (if we still have it) after delay
            setTimeout(() => {
                const campaignData = localStorage.getItem('MOTITO');
                if (campaignData) {
                    const storedCampaign = JSON.parse(campaignData);
                    if (storedCampaign?.id) {
                        navigate(`/${storedCampaign.id}`);
                        return;
                    }
                }
                window.location.assign('https://motito.co');
            }, 3000);
            return;
        }

        try {
            setMessage('Verifying your payment...');

            // Poll for contribution status
            const result = await pollContributionStatus(contributionId, {
                maxAttempts: 15,
                intervalMs: 2000,
                onPoll: (attempt, status) => {
                    console.log(`Poll attempt ${attempt}:`, status.status);
                    if (String(status.status || '').toUpperCase() === 'PROCESSING') {
                        setMessage('Processing your payment...');
                    }
                },
            });

            console.log('Final contribution status:', result);

            const normalized = String(result.status || '').toUpperCase();
            if (normalized === 'COMPLETED') {
                setStatus('success');
                setMessage('Payment successful!');
                // Clear stored contribution ID
                localStorage.removeItem('MOTITO_CONTRIBUTION_ID');
                // Navigate to success page
                navigate('/success');
            } else if (['FAILED', 'CANCELLED', 'REFUNDED'].includes(normalized)) {
                setStatus('failed');
                setMessage('Payment was not completed');
                toast({
                    title: 'Payment not completed',
                    description: 'Your payment was not completed. Please try again.',
                    status: 'error',
                    duration: 5000,
                    position: 'top',
                });
                // Get campaign context and redirect back
                const campaignData = localStorage.getItem('MOTITO');
                if (campaignData) {
                    const campaign = JSON.parse(campaignData);
                    if (campaign?.id) {
                        navigate(`/pay/${campaign.id}`);
                    } else {
                        window.location.assign('https://motito.co');
                    }
                } else {
                    window.location.assign('https://motito.co');
                }
            } else {
                // Still pending after max attempts
                setStatus('failed');
                setMessage('Payment verification timed out');
                toast({
                    title: 'Verification timeout',
                    description: 'Please check your payment status later.',
                    status: 'warning',
                    duration: 5000,
                    position: 'top',
                });
            }
        } catch (error) {
            console.error('Verification error:', error);
            setStatus('failed');
            setMessage('Failed to verify payment');
            toast({
                title: 'Verification error',
                description: 'Could not verify your payment. Please contact support.',
                status: 'error',
                duration: 5000,
                position: 'top',
            });
        }
    }, [navigate, toast]);

    useEffect(() => {
        void verifyPayment();
    }, [verifyPayment]);

    return (
        <div>
            <Header />
            <div className="max-w-[500px] px-4 mx-auto space-y-4 mt-16 text-center">
                <Spinner size="xl" color="blue.500" />
                <p className="text-lg font-medium text-[#0E1736]">{message}</p>
                {status === 'verifying' && (
                    <p className="text-sm text-[#5C6278]">
                        Please wait while we confirm your payment...
                    </p>
                )}
            </div>
        </div>
    );
}

export default PaymentCallback;
